import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { useStoreGetters, useMapGetter } from 'dashboard/composables/store';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';

import {
  getActionOptions,
  getConditionOptions,
} from 'dashboard/helper/automationHelper';
import {
  MESSAGE_CONDITION_VALUES,
  PRIORITY_CONDITION_VALUES,
  TRAVEL_TRIP_STATUS_VALUES,
  TRAVEL_HOLD_STATE_VALUES,
  CASE_STAGE_CATEGORY_VALUES,
} from 'dashboard/constants/automation';

/**
 * This is a shared composables that holds utilities used to build dropdown and file options
 * @returns {Object} An object containing various automation-related functions and computed properties.
 */
export default function useAutomationValues() {
  const getters = useStoreGetters();
  const { t } = useI18n();
  const agents = useMapGetter('agents/getVerifiedAgents');
  const campaigns = useMapGetter('campaigns/getAllCampaigns');
  const contacts = useMapGetter('contacts/getContacts');
  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabels');
  const teams = useMapGetter('teams/getTeams');
  const slaPolicies = useMapGetter('sla/getSLA');
  const casesStore = useSupportCasesStore();

  const booleanFilterOptions = computed(() => [
    { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
    { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
  ]);

  const statusFilterItems = computed(() => {
    return {
      open: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
      },
      resolved: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
      },
      pending: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
      },
      snoozed: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
      },
      all: {
        TEXT: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
      },
    };
  });

  const statusFilterOptions = computed(() => {
    const statusFilters = statusFilterItems.value;
    return [
      ...Object.keys(statusFilters).map(status => ({
        id: status,
        name: statusFilters[status].TEXT,
      })),
      { id: 'all', name: t('CHAT_LIST.FILTER_ALL') },
    ];
  });

  const messageTypeOptions = computed(() =>
    MESSAGE_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.MESSAGE_TYPES.${item.i18nKey}`),
    }))
  );

  const priorityOptions = computed(() =>
    PRIORITY_CONDITION_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.PRIORITY_TYPES.${item.i18nKey}`),
    }))
  );

  const travelTripStatusOptions = computed(() =>
    TRAVEL_TRIP_STATUS_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.TRAVEL_TRIP_STATUSES.${item.i18nKey}`),
    }))
  );

  const travelHoldStateOptions = computed(() =>
    TRAVEL_HOLD_STATE_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.TRAVEL_HOLD_STATES.${item.i18nKey}`),
    }))
  );

  const caseTypeOptions = computed(() =>
    casesStore.caseTypeOptions.map(caseType => ({
      id: caseType.id,
      name: caseType.name,
    }))
  );

  // One flat list across every ticket type rather than a list per type: the rule is not
  // scoped to a type unless it also says so, and a stage picker that first made you pick a
  // type would be inventing a condition the rule does not have. Each option names the type
  // it belongs to because two workflows can both have a stage called "In progress" and a
  // stage id says nothing on its own.
  const caseStageOptions = computed(() =>
    casesStore.caseTypeOptions.flatMap(caseType =>
      (caseType.workflowStages || []).map(stage => ({
        id: stage.id,
        name: `${caseType.name} › ${stage.name}`,
      }))
    )
  );

  const caseStageCategoryOptions = computed(() =>
    CASE_STAGE_CATEGORY_VALUES.map(item => ({
      id: item.id,
      name: t(`AUTOMATION.CASE_STAGE_CATEGORIES.${item.i18nKey}`),
    }))
  );

  // The same priorities a conversation offers, minus the magic 'nil' that stands for "no
  // priority": a case rule says that with is_not_present, which is a real operator the
  // filter service already understands rather than a value it would have to special-case.
  const casePriorityOptions = computed(() =>
    priorityOptions.value.filter(option => option.id !== 'nil')
  );

  /**
   * Adds a translated "None" option to the beginning of a list
   * @param {Array} list - The list to add "None" to
   * @returns {Array} A new array with "None" option at the beginning
   */
  const addNoneToList = list => [
    {
      id: 'nil',
      name: t('AUTOMATION.NONE_OPTION') || 'None',
    },
    ...(list || []),
  ];

  /**
   * Gets the condition dropdown values for a given type.
   * @param {string} type - The type of condition.
   * @returns {Array} An array of condition dropdown values.
   */
  const getConditionDropdownValues = type => {
    return getConditionOptions({
      agents: agents.value,
      booleanFilterOptions: booleanFilterOptions.value,
      campaigns: campaigns.value,
      contacts: contacts.value,
      customAttributes: getters['attributes/getAttributes'].value,
      inboxes: inboxes.value,
      labels: labels.value,
      statusFilterOptions: statusFilterOptions.value,
      priorityOptions: priorityOptions.value,
      messageTypeOptions: messageTypeOptions.value,
      travelTripStatusOptions: travelTripStatusOptions.value,
      travelHoldStateOptions: travelHoldStateOptions.value,
      caseTypeOptions: caseTypeOptions.value,
      caseStageOptions: caseStageOptions.value,
      caseStageCategoryOptions: caseStageCategoryOptions.value,
      casePriorityOptions: casePriorityOptions.value,
      teams: teams.value,
      languages,
      countries,
      type,
    });
  };

  /**
   * Gets the action dropdown values for a given type.
   * @param {string} type - The type of action.
   * @returns {Array} An array of action dropdown values.
   */
  const getActionDropdownValues = type => {
    let agentsList = agents.value;
    if (type === 'assign_agent') {
      agentsList = [
        {
          id: 'last_responding_agent',
          name: t('AUTOMATION.LAST_RESPONDING_AGENT'),
        },
        ...agentsList,
      ];
    }

    return getActionOptions({
      agents: agentsList,
      labels: labels.value,
      teams: teams.value,
      slaPolicies: slaPolicies.value,
      languages,
      type,
      addNoneToListFn: addNoneToList,
      priorityOptions: priorityOptions.value,
      // The same flat "Type › Stage" list the `case_stage_id` condition offers: a rule that
      // moves a ticket picks from the stages the account actually has.
      caseStageOptions: caseStageOptions.value,
    });
  };

  return {
    booleanFilterOptions,
    statusFilterItems,
    statusFilterOptions,
    priorityOptions,
    messageTypeOptions,
    getConditionDropdownValues,
    getActionDropdownValues,
    agents,
    campaigns,
    contacts,
    inboxes,
    labels,
    teams,
    slaPolicies,
  };
}
